import React, { useEffect, useMemo, useState } from "react";
import { getEntityKpis } from "../../api/kpi.api";
import KpiBadge from "./KpiBadge";
import { useNavigate } from "react-router-dom";
import { KPICopy } from "../../copy/kpiCopy";
import { useAuth } from "../../useAuth";
import { canEditKpis, canViewKpis } from "../../accessControl";
import Button from "../../ui/Button";
import { formatKpiValueWithUnit } from "../../utils/kpiUnits";

export default function RelatedKpisPanel({ entityType, entityId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth() || {};
  const canView = useMemo(() => canViewKpis(user), [user]);
  const canEdit = useMemo(() => canEditKpis(user), [user]);

  useEffect(() => {
    if (!canView || !entityType || !entityId) {
      setItems([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    getEntityKpis(entityType, entityId)
      .then((res) => setItems(res.data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [canView, entityType, entityId]);

  const order = { red: 0, yellow: 1, green: 2, unknown: 3, stale: 4 };
  const ranked = [...items]
    .sort((a, b) => {
      const aRank = order[(a.latest_rag || "unknown").toLowerCase()] ?? 3;
      const bRank = order[(b.latest_rag || "unknown").toLowerCase()] ?? 3;
      return aRank - bRank;
    })
    .slice(0, 5);

  if (!canView) return null;

  return (
    <div style={{ border: "1px solid #e5e7eb", borderRadius: "10px", padding: "12px", background: "white" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px" }}>
        <div>
          <h4 style={{ margin: 0, fontSize: "14px" }}>Related KPIs</h4>
          <div style={{ marginTop: "4px", fontSize: "12px", color: "#6b7280" }}>{KPICopy.relatedSubtitle}</div>
        </div>
        <Button
          onClick={() => navigate("/kpis")}
          variant="ghost"
          style={{ border: "none", background: "transparent", color: "#10b2aa", cursor: "pointer" }}
        >
          View all
        </Button>
      </div>
      {loading && <div style={{ fontSize: "12px", color: "#6b7280" }}>Loading...</div>}
      {!loading && items.length === 0 && (
        <div style={{ fontSize: "12px", color: "#6b7280" }}>{KPICopy.relatedEmpty}</div>
      )}
      {!loading &&
        ranked.map((kpi) => (
          <div
            key={kpi.id}
            data-testid="related-kpi-row"
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "8px 0",
              borderBottom: "1px solid #f3f4f6",
            }}
          >
            <div>
              <div style={{ fontWeight: 600 }}>{kpi.name}</div>
              <div style={{ fontSize: "12px", color: "#6b7280" }}>
                {kpi.latest_value !== null && kpi.latest_value !== undefined
                  ? formatKpiValueWithUnit(kpi.latest_value, kpi.unit)
                  : "No data"}
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <KpiBadge value={kpi.latest_rag} />
              {String(kpi.latest_rag || "").toLowerCase() === "stale" && (
                <span style={{ fontSize: "11px", color: "#6b7280" }}>Stale</span>
              )}
              <Button
                onClick={() => navigate(`/kpis/${kpi.id}`)}
                variant="secondary"
                style={{
                  padding: "6px 10px",
                  borderRadius: "6px",
                  border: "1px solid #d1d5db",
                  background: "#f9fafb",
                  fontSize: "12px",
                }}
              >
                Open
              </Button>
            </div>
          </div>
        ))}
      <div style={{ marginTop: "8px", fontSize: "12px", color: "#6b7280" }}>
        {KPICopy.relatedCaption}
      </div>
      {!canEdit && (
        <div style={{ marginTop: "4px", fontSize: "12px", color: "#9ca3af" }}>
          {KPICopy.readOnlyNotice}
        </div>
      )}
    </div>
  );
}
